import React, {useMemo} from "react";
import dayjs from "dayjs";
import {FormattedMessage} from "react-intl";
import {twJoin} from "tailwind-merge";
import {
    axisHours,
    CartInTimeline,
    CartKind,
    formatTime,
    minutesOfDay,
    toLanes
} from "@widgets/reports/cartTimeline/CartTimelineUtils";
import CartTimelineBar, {barClassNames} from "@widgets/reports/cartTimeline/timeline/CartTimelineBar";
import useTimelineHover from "@widgets/reports/cartTimeline/timeline/useTimelineHover";

const legendKinds: CartKind[] = ["active", "paying", "ordered", "expired", "reset", "pos"];

/** Width of the column with the lane labels. */
const labelColClassName = "w-16 shrink-0";

type Props = {
    carts: CartInTimeline[],
    /** All carts of the day (also the filtered out ones), so the axis stays the same. */
    allCarts: CartInTimeline[],
    day: Date,
    /** `null` when the day isn't today: no "now" line. */
    now: Date | null,
    onSelect: (item: CartInTimeline) => void,
};

/**
 * The carts of a day as bars on a time axis, overlapping carts under each
 * other, with a line at the current time.
 */
const CartTimelineChart = ({carts, allCarts, day, now, onSelect}: Props) => {
    const {from, to} = useMemo(() => axisHours(allCarts, day), [allCarts, day]);
    const lanes: CartInTimeline[][] = useMemo(() => toLanes(carts), [carts]);
    const span: number = (to - from) * 60;
    const toPercentage = (minutes: number): number =>
        Math.max(0, Math.min(100, (minutes - from * 60) / span * 100));
    const hours: number[] = Array.from({length: to - from + 1}, (_, i) => from + i);
    const nowLeft: number | null = now === null ? null : toPercentage(minutesOfDay(now, day));
    const {trackRef, hoverLeft, onMouseMove, onMouseLeave} = useTimelineHover();
    const hoverTime: Date | null = hoverLeft === null
        ? null
        : dayjs(day).startOf("day").add(from * 60 + hoverLeft / 100 * span, "minute").toDate();

    return (
        <div>
            <div className={"overflow-x-auto"}>
                {/* Padding on the right, so the label of the last hour fits */}
                <div
                    className={"relative min-w-[720px] pr-4"}
                    onMouseMove={onMouseMove}
                    onMouseLeave={onMouseLeave}
                >
                    {/* Behind the bars: hour lines and the rest of today (still to come) */}
                    <div
                        ref={trackRef}
                        aria-hidden={true}
                        className={"absolute top-6 bottom-0 left-16 right-4 pointer-events-none"}
                    >
                        {hours.map((hour: number) => (
                            <span
                                key={hour}
                                className={"absolute inset-y-0 w-px bg-gray-100 dark:bg-slate-700/50"}
                                style={{left: `${toPercentage(hour * 60)}%`}}
                            />
                        ))}
                        {
                            nowLeft !== null &&
                            <span
                                className={"absolute inset-y-0 right-0 bg-gray-50/70 dark:bg-slate-700/40"}
                                style={{left: `${nowLeft}%`}}
                            />
                        }
                    </div>

                    {/* Hours, with the current time */}
                    <div className={"flex h-6"}>
                        <span className={labelColClassName}/>
                        <div className={"relative flex-1"}>
                            {hours.map((hour: number) => (
                                <span
                                    key={hour}
                                    className={"absolute top-1 -translate-x-1/2 text-[11px] tabular-nums text-gray-500 dark:text-slate-400"}
                                    style={{left: `${toPercentage(hour * 60)}%`}}
                                >
                                    {String(hour).padStart(2, "0")}:00
                                </span>
                            ))}
                            {
                                nowLeft !== null &&
                                <span
                                    className={"absolute top-0.5 -translate-x-1/2 px-1.5 rounded bg-red-600 text-white text-[11px] leading-5 font-semibold tabular-nums z-[1]"}
                                    style={{left: `${nowLeft}%`}}
                                >
                                    {formatTime(now!)}
                                </span>
                            }
                            {
                                hoverLeft !== null &&
                                <span
                                    className={"absolute top-0.5 -translate-x-1/2 px-1.5 rounded bg-gray-900 text-white text-[11px] leading-5 font-semibold tabular-nums z-[2] pointer-events-none dark:bg-slate-100 dark:text-slate-900"}
                                    style={{left: `${hoverLeft}%`}}
                                >
                                    {formatTime(hoverTime!)}
                                </span>
                            }
                        </div>
                    </div>

                    {/* Lanes */}
                    <div className={"relative flex flex-col gap-1.5 py-2"}>
                        {
                            lanes.length === 0
                                ? (
                                    <div className={"flex items-center h-[26px]"}>
                                        <span className={labelColClassName}/>
                                        <span className={"text-sm text-gray-500 dark:text-slate-400"}>
                                            <FormattedMessage id={"cartTimeline.noCartsInFilter"}/>
                                        </span>
                                    </div>
                                )
                                : lanes.map((lane: CartInTimeline[], index: number) => (
                                    <div key={index} className={"flex items-center h-[26px]"}>
                                        <span className={twJoin(labelColClassName, "text-[11px] text-gray-400 dark:text-slate-500")}>
                                            {index === 0 && <FormattedMessage id={"cartTimeline.carts"}/>}
                                        </span>
                                        <div className={"relative flex-1 h-[26px]"}>
                                            {lane.map((item: CartInTimeline) => {
                                                const left: number = toPercentage(minutesOfDay(item.start, day));
                                                const right: number = toPercentage(minutesOfDay(item.end, day));
                                                return (
                                                    <CartTimelineBar
                                                        key={item.cart.id}
                                                        item={item}
                                                        left={left}
                                                        width={Math.max(right - left, 0)}
                                                        onSelect={onSelect}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                        }
                    </div>

                    {/* In front of the bars: the lines at the current and the hovered time, from their label down */}
                    <div aria-hidden={true} className={"absolute top-5 bottom-0 left-16 right-4 pointer-events-none"}>
                        {
                            nowLeft !== null &&
                            <span
                                className={"absolute inset-y-0 w-0.5 -translate-x-1/2 rounded bg-red-600"}
                                style={{left: `${nowLeft}%`}}
                            />
                        }
                        {
                            hoverLeft !== null &&
                            <span
                                className={"absolute inset-y-0 -translate-x-1/2 border-l border-dashed border-gray-900/60 dark:border-slate-100/60"}
                                style={{left: `${hoverLeft}%`}}
                            />
                        }
                    </div>
                </div>
            </div>

            {/* Legend, outside of the scroll container so it stays visible on small screens */}
            <ul className={"flex flex-wrap gap-x-5 gap-y-2 mt-3 sm:ml-16 text-xs text-gray-600 dark:text-slate-300"}>
                {legendKinds.map((kind: CartKind) => (
                    <li key={kind} className={"inline-flex items-center gap-1.5"}>
                        <span
                            aria-hidden={true}
                            className={twJoin(
                                kind === "pos" ? "size-2.5 rounded-full" : "w-3.5 h-2.5 rounded-sm",
                                barClassNames[kind].replace("animate-pulse", ""),
                            )}
                        />
                        <FormattedMessage id={`cartTimeline.kind.${kind}`}/>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default CartTimelineChart;